import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import * as XLSX from 'xlsx-js-style';
import useSocketIO from '../../hooks/useSocketIO';
import useHttp from '../../hooks/useHttp';
import { useAuth } from '../../context/authContext';
import useBranch from '../../hooks/useBranch';
import { buildRolloutIndex } from '../../data/regiondoRolloutOrder';

const steps = [
  { key: 'compare', title: 'Vergleich & Login' },
  { key: 'match', title: 'Matching (Voucher-Typen → RVP)' },
  { key: 'checks', title: 'Konsistenzchecks (Dry-Run)' },
  { key: 'import', title: 'Import (Live)' },
];

function hasAnyMatchingDraft(draft) {
  const d = draft && typeof draft === 'object' ? draft : {};
  const a =
    d.resourceVoucherProductByVoucherTypeKey && typeof d.resourceVoucherProductByVoucherTypeKey === 'object'
      ? d.resourceVoucherProductByVoucherTypeKey
      : {};
  const b =
    d.resourceVoucherProductByVoucherTypePriceKey && typeof d.resourceVoucherProductByVoucherTypePriceKey === 'object'
      ? d.resourceVoucherProductByVoucherTypePriceKey
      : {};
  const c = d.ignoredVoucherTypeKeys && typeof d.ignoredVoucherTypeKeys === 'object' ? d.ignoredVoucherTypeKeys : {};

  if (Object.keys(a).length) return true;
  if (Object.keys(b).length) return true;
  if (Object.values(c).some(v => v === true)) return true;
  return false;
}

function normalizeMatchingDraftFromServer(matching) {
  const m = matching && typeof matching === 'object' ? matching : {};
  return {
    resourceVoucherProductByVoucherTypeKey: m.resourceVoucherProductByVoucherTypeKey || {},
    resourceVoucherProductByVoucherTypePriceKey: m.resourceVoucherProductByVoucherTypePriceKey || {},
    ignoredVoucherTypeKeys: m.ignoredVoucherTypeKeys || {},
  };
}

function mergeAiSuggestionsIntoDraft(prevDraft, aiSuggestions, voucherTypePriceKeysByVoucherTypeKey) {
  const prev = prevDraft && typeof prevDraft === 'object' ? prevDraft : {};
  const ai = aiSuggestions && typeof aiSuggestions === 'object' ? aiSuggestions : {};

  const aiMap = ai?.resourceVoucherProductByVoucherTypeKey || {};
  const aiPriceMap = ai?.resourceVoucherProductByVoucherTypePriceKey || {};
  const aiIgnored = ai?.ignoredVoucherTypeKeys || {};

  const existingMap = prev.resourceVoucherProductByVoucherTypeKey || {};
  const existingPriceMap = prev.resourceVoucherProductByVoucherTypePriceKey || {};
  const existingIgnored = prev.ignoredVoucherTypeKeys || {};

  let didChange = false;

  const mergedMap = { ...existingMap };
  for (const [k, v] of Object.entries(aiMap)) {
    const cur = existingMap?.[k];
    if (!cur) {
      mergedMap[k] = String(v || '');
      didChange = true;
    }
  }

  const mergedPriceMap = { ...existingPriceMap };
  for (const [k, v] of Object.entries(aiPriceMap)) {
    const cur = existingPriceMap?.[k];
    if (!cur) {
      mergedPriceMap[k] = String(v || '');
      didChange = true;
    }
  }
  for (const [voucherTypeKey, rvpId] of Object.entries(aiMap)) {
    const suggested = String(rvpId || '').trim();
    if (!suggested) continue;
    const priceKeys = voucherTypePriceKeysByVoucherTypeKey?.get?.(String(voucherTypeKey || '').trim());
    if (!Array.isArray(priceKeys) || priceKeys.length === 0) continue;
    for (const pk of priceKeys) {
      const cur = existingPriceMap?.[pk];
      if (!cur) {
        mergedPriceMap[pk] = suggested;
        didChange = true;
      }
    }
  }

  const mergedIgnored = { ...existingIgnored };
  for (const [k, v] of Object.entries(aiIgnored)) {
    if (existingMap?.[k]) continue;
    if (existingIgnored?.[k] === true) continue;
    if (v === true) {
      mergedIgnored[k] = true;
      didChange = true;
    }
  }

  const nextDraft = {
    ...prev,
    resourceVoucherProductByVoucherTypeKey: mergedMap,
    resourceVoucherProductByVoucherTypePriceKey: mergedPriceMap,
    ignoredVoucherTypeKeys: mergedIgnored,
  };

  return { nextDraft, didChange };
}

function shortenMiddle(value, left = 10, right = 8) {
  const s = String(value ?? '');
  if (!s) return '';
  const l = Math.max(0, Math.floor(left));
  const r = Math.max(0, Math.floor(right));
  if (s.length <= l + r + 3) return s;
  return `${s.slice(0, l)}…${s.slice(-r)}`;
}

function deriveStepPhaseFromSession(session) {
  const status = String(session?.status || '').trim();
  const hasDryRun = Boolean(session?.resultData?.dryRun);
  const hasMatching = Boolean(session?.matching?.lockedAt);
  const pct = Number(session?.progressPercentage || 0);

  if (status === 'importing' || status === 'finished') return 'import';
  if (hasDryRun || status === 'readyForImport') return 'checks';
  if (status === 'readyForDryRun') {
    return pct >= 55 ? 'checks' : 'match';
  }
  if (hasMatching || status === 'needsMatching' || status === 'compareFinished')
    return 'match';
  if (status === 'comparing' || status === 'created') return 'compare';
  return 'compare';
}

function StatCard({ title, value, subtitle, color = 'default', isDone = false, action = null }) {
  const theme = useTheme();
  return (
    <Card
      variant='outlined'
      sx={{
        height: '100%',
        borderColor: isDone ? 'success.main' : 'divider',
        backgroundImage: isDone
          ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.22)}, ${alpha(
              theme.palette.success.main,
              0.05
            )})`
          : 'none',
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant='overline' color='text.secondary'>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant='h4' sx={{ fontWeight: 700, letterSpacing: -0.5 }}>
              {value}
            </Typography>
            <Chip size='small' color={color} label={subtitle || ''} sx={{ opacity: subtitle ? 1 : 0 }} />
          </Box>
        </Box>
        {action ? action : isDone ? <CheckCircleIcon color='success' /> : null}
      </CardContent>
    </Card>
  );
}

function decodeKey(key) {
  try {
    const padded = String(key).replace(/-/g, '+').replace(/_/g, '/');
    const padLen = (4 - (padded.length % 4)) % 4;
    const raw = padded + '='.repeat(padLen);
    return JSON.parse(atob(raw));
  } catch {
    return null;
  }
}

function isDryRunRunningFromStatus(status) {
  const phase = String(status?.phase || '').trim();
  const title = String(status?.title || '').toLowerCase();
  if (phase !== 'checks') return false;
  if (title.includes('dry-run läuft')) return true;
  if (title.includes('dry run läuft')) return true;
  return false;
}

function isImportRunningFromStatus(status) {
  const phase = String(status?.phase || '').trim();
  const title = String(status?.title || '').toLowerCase();
  if (phase !== 'import') return false;
  if (title.includes('import läuft')) return true;
  return false;
}

function isCompareRunningFromStatus(status) {
  const phase = String(status?.phase || '').trim();
  const title = String(status?.title || '').toLowerCase();
  if (phase !== 'compare') return false;
  if (title.includes('vergleich läuft')) return true;
  return false;
}

function renderDryRunItemReason(it) {
  const skipped = String(it?.skipped || '').trim();
  if (skipped === 'missingTypeMapping') return 'Kein Matching (Voucher-Typ nicht zugeordnet)';
  if (skipped === 'alreadyImported') return 'Bereits importiert';
  if (skipped === 'ignoredType') return 'Typ ignoriert';
  if (it?.error) return `Scrape-Fehler: ${String(it.error)}`;
  if (it?.isUsed === true && it?.isUsedValidated === true) return 'Bereits eingelöst (Weg B: XLS)';
  if (it?.isUsed === true && it?.isUsedScrape === true) return 'Bereits eingelöst (Weg A: Scrape)';
  if (it?.isUsed === true) return 'Bereits eingelöst';
  return 'Importierbar';
}

function formatEUR(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  try {
    return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  } catch {
    return `${n.toFixed(2)} €`;
  }
}

function toPriceCents(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

function voucherTypePriceKey(voucherTypeKey, priceInclTax) {
  const k = String(voucherTypeKey || '').trim();
  const cents = toPriceCents(priceInclTax);
  if (!k || cents == null) return '';
  return `${k}__${cents}`;
}

function safeLowerString(v) {
  return String(v ?? '').trim().toLowerCase();
}

function extractRegiondoValidatedTicketCodesFromRows(rows) {
  const table = Array.isArray(rows) ? rows : [];
  if (table.length === 0) return [];

  const headerRow = Array.isArray(table[0]) ? table[0] : [];
  const header = headerRow.map(c => safeLowerString(c));

  let ticketCodeCol = header.findIndex(h => h === 'ticketcode' || h === 'ticket code' || h.includes('ticketcode'));
  if (ticketCodeCol < 0) ticketCodeCol = 0;

  const out = new Set();
  for (let i = 1; i < table.length; i++) {
    const row = Array.isArray(table[i]) ? table[i] : [];
    const raw = row[ticketCodeCol];
    const code = safeLowerString(raw);
    if (!code) continue;
    out.add(code);
  }

  return [...out.values()].sort((a, b) => a.localeCompare(b));
}

export default function RegiondoVoucherMigration() {
  const theme = useTheme();
  const vmSurface = useMemo(() => {
    const isLight = theme.palette.mode === 'light';
    const d = theme.palette.divider;
    const w = theme.palette.common.white;
    return {
      iconBtnOutline: `1px solid ${isLight ? d : alpha(w, 0.22)}`,
      linearBg: isLight ? theme.palette.grey[200] : alpha(w, 0.08),
      panelBorder: `1px solid ${isLight ? d : alpha(w, 0.1)}`,
      panelBg: isLight ? theme.palette.grey[50] : alpha(w, 0.02),
      rowBorderSel: `1px solid ${
        isLight ? theme.palette.primary.main : alpha(theme.palette.primary.light, 0.75)
      }`,
      rowBorder: `1px solid ${isLight ? d : alpha(w, 0.06)}`,
      rowBgSel: isLight
        ? alpha(theme.palette.primary.main, 0.08)
        : alpha(theme.palette.primary.light, 0.1),
      rowBg: isLight ? 'transparent' : alpha(w, 0.02),
      finalizeBg: isLight ? theme.palette.grey[100] : alpha(w, 0.04),
    };
  }, [theme]);

  const auth = useAuth();
  const { sendRequest } = useHttp();
  const { emit, on, off, isConnected } = useSocketIO();
  const { getBranches } = useBranch();

  const readLS = useCallback((key, fallback = '') => {
    try {
      const v = window?.localStorage?.getItem?.(key);
      return v != null ? String(v) : fallback;
    } catch (_) {
      return fallback;
    }
  }, []);

  const writeLS = useCallback((key, value) => {
    try {
      if (value == null || String(value).trim() === '') {
        window?.localStorage?.removeItem?.(key);
      } else {
        window?.localStorage?.setItem?.(key, String(value));
      }
    } catch (_) {}
  }, []);

  const [activeStep, setActiveStep] = useState(0);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(() => readLS('regiondoVoucherMigration.branchId', ''));

  const [dateFrom, setDateFrom] = useState(dayjs().subtract(6, 'month').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(dayjs().add(3, 'year').format('YYYY-MM-DD'));

  const [regiondoUsername, setRegiondoUsername] = useState('');
  const [regiondoPassword, setRegiondoPassword] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);

  const [sessionId, setSessionId] = useState(null);
  const [sessionMeta, setSessionMeta] = useState({ status: '', progressPercentage: 0 });
  const [regiondoAuthInfo, setRegiondoAuthInfo] = useState({ hasCookie: false, usernameHint: '' });
  const [status, setStatus] = useState({ phase: 'idle', progress: 0, title: 'Bereit', details: '' });
  const [progressInfo, setProgressInfo] = useState(null);
  const [stats, setStats] = useState({});
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);

  const [isStarting, setIsStarting] = useState(false);
  const [isRefreshingCompare, setIsRefreshingCompare] = useState(false);
  const [isEditingRegiondoLogin, setIsEditingRegiondoLogin] = useState(false);
  const [isRegiondoLoggingIn, setIsRegiondoLoggingIn] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isLoadingSavedSessions, setIsLoadingSavedSessions] = useState(false);
  const [isSavingMatching, setIsSavingMatching] = useState(false);
  const [isDryRunning, setIsDryRunning] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isAiSuggesting, setIsAiSuggesting] = useState(false);

  const [savedSessions, setSavedSessions] = useState([]);
  const [savedMatchingSessionId, setSavedMatchingSessionId] = useState('');
  const [isApplyingSavedMatchingOnly, setIsApplyingSavedMatchingOnly] = useState(false);

  const [matchingContext, setMatchingContext] = useState(null);
  const [resourceVoucherProducts, setResourceVoucherProducts] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [matchingDraft, setMatchingDraft] = useState({
    resourceVoucherProductByVoucherTypeKey: {},
    resourceVoucherProductByVoucherTypePriceKey: {},
    ignoredVoucherTypeKeys: {},
  });

  const [dryRunReport, setDryRunReport] = useState(null);
  const [importReport, setImportReport] = useState(null);
  const [dryRunDetailsOpen, setDryRunDetailsOpen] = useState(false);
  const [dryRunDetailsTab, setDryRunDetailsTab] = useState('summary');
  const [dryRunSearch, setDryRunSearch] = useState('');
  const [dryRunSelectedTicketCode, setDryRunSelectedTicketCode] = useState('');
  const [importDetailsOpen, setImportDetailsOpen] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [finalizeDialogMode, setFinalizeDialogMode] = useState('do');
  const [isFinalizingBranch, setIsFinalizingBranch] = useState(false);

  const validatedUsedFileInputRef = useRef(null);
  const [validatedUsedInfo, setValidatedUsedInfo] = useState({
    count: 0,
    updatedAt: null,
    updatedBy: null,
    sourceLabel: '',
  });
  const [isSavingValidatedUsed, setIsSavingValidatedUsed] = useState(false);

  const lastJoinedSessionIdRef = useRef(null);
  const lastAppliedAiSigRef = useRef('');
  const lastAutoSavedAiSigRef = useRef('');
  const matchingDirtyRef = useRef(false);
  const userStepOverrideRef = useRef(false);
  const lastAutoStepSessionIdRef = useRef('');
  const lastResumeAutoStepAtRef = useRef(0);

  const markMatchingDirty = useCallback(() => {
    matchingDirtyRef.current = true;
  }, []);

  const hasRight = auth?.checkRightAny?.('internalBooking:migration:regiondo');

  const buildRegiondoExternalVoucherUrl = useCallback(ticketCode => {
    const code = String(ticketCode || '').trim();
    if (!code) return '';
    return `https://www.regiondo.de/resellers/account/codes/search/${encodeURIComponent(code)}`;
  }, []);

  const stepKey = steps[activeStep]?.key || 'compare';
  const isRegiondoAlreadyAuthenticated = Boolean(sessionId && regiondoAuthInfo?.hasCookie);
  const isRegiondoLoginEditable = !isRegiondoAlreadyAuthenticated || isEditingRegiondoLogin;

  const pushLog = useCallback(line => {
    if (!line) return;
    setLogs(prev => [...prev, { ts: Date.now(), line: String(line) }].slice(-700));
  }, []);

  const resetSessionUi = useCallback(() => {
    setSessionId(null);
    setMatchingContext(null);
    setAiSuggestions(null);
    lastAppliedAiSigRef.current = '';
    lastAutoSavedAiSigRef.current = '';
    matchingDirtyRef.current = false;
    userStepOverrideRef.current = false;
    lastAutoStepSessionIdRef.current = '';
    setIsEditingRegiondoLogin(false);
    setMatchingDraft({
      resourceVoucherProductByVoucherTypeKey: {},
      resourceVoucherProductByVoucherTypePriceKey: {},
      ignoredVoucherTypeKeys: {},
    });
    setDryRunReport(null);
    setImportReport(null);
    setSavedSessions([]);
    setLogs([]);
    setError(null);
    setStatus({ phase: 'idle', progress: 0, title: 'Bereit', details: '' });
    setProgressInfo(null);
    setStats({});
    setValidatedUsedInfo({ count: 0, updatedAt: null, updatedBy: null, sourceLabel: '' });
    setActiveStep(0);
    setVisitedMaxStep(0);
  }, []);

  const loadBranches = useCallback(async () => {
    try {
      const b = await getBranches();
      const all = Array.isArray(b) ? b : [];
      const { byBranchId } = buildRolloutIndex();
      const filtered = showCompleted ? all : all.filter(x => !x?.regiondoVoucherMigration?.completedAt);
      const sorted = [...filtered].sort((a, b2) => {
        const ai = byBranchId.has(String(a?._id)) ? byBranchId.get(String(a._id)) : 9999;
        const bi = byBranchId.has(String(b2?._id)) ? byBranchId.get(String(b2._id)) : 9999;
        if (ai !== bi) return ai - bi;
        const an = String(a?.locationName || a?.longName || '').localeCompare(
          String(b2?.locationName || b2?.longName || ''),
          undefined,
          { sensitivity: 'base' }
        );
        if (an !== 0) return an;
        return String(a?._id || '').localeCompare(String(b2?._id || ''));
      });
      setBranches(sorted);
      setSelectedBranchId(prev => {
        const cur = String(prev || '');
        if (cur && sorted.some(x => String(x?._id) === cur)) return cur;
        if (sorted.length === 1) return String(sorted[0]?._id || '');
        if (!cur) return '';
        return sorted[0]?._id ? String(sorted[0]._id) : '';
      });
    } catch (e) {
      toast.error(e?.message || 'Branches konnten nicht geladen werden.');
    }
  }, [getBranches, showCompleted]);

  const joinSessionRoom = useCallback(
    sid => {
      const s = String(sid || '').trim();
      if (!s) return;
      if (!isConnected) return;
      emit('join_migration', { sessionId: s });
      lastJoinedSessionIdRef.current = s;
    },
    [emit, isConnected]
  );

  const loadSession = useCallback(
    async sid => {
      const s = String(sid || '').trim();
      if (!s) return;
      setIsLoadingSession(true);
      try {
        const r = await sendRequest({
          url: `/api/v1/regiondoVoucherMigration/session/${s}`,
          method: 'GET',
          headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token },
        });
        if (!r?.success) throw new Error('Session load failed.');
        const session = r.session;
        const validatedCount = Array.isArray(session?.validatedUsedTicketCodes) ? session.validatedUsedTicketCodes.length : 0;
        const validatedMeta = session?.validatedUsedMeta && typeof session.validatedUsedMeta === 'object' ? session.validatedUsedMeta : {};
        setValidatedUsedInfo({
          count: validatedCount,
          updatedAt: validatedMeta?.updatedAt || null,
          updatedBy: validatedMeta?.updatedBy || null,
          sourceLabel: validatedMeta?.sourceLabel ? String(validatedMeta.sourceLabel) : '',
        });

        setSessionMeta({
          status: session?.status ? String(session.status) : '',
          progressPercentage: Number(session?.progressPercentage || 0),
        });

        const phase = deriveStepPhaseFromSession(session);
        const stepIndex = Math.max(
          0,
          steps.findIndex(x => x.key === phase)
        );
        setActiveStep(prev => {
          const next = stepIndex === -1 ? 0 : stepIndex;
          if (lastAutoStepSessionIdRef.current !== s) {
            lastAutoStepSessionIdRef.current = s;
            userStepOverrideRef.current = false;
            return next;
          }
          if (userStepOverrideRef.current) return prev;
          return next;
        });

        setStatus(prev => ({
          ...prev,
          phase,
          title: session?.status ? `Status: ${session.status}` : prev.title,
          details: '',
          progress: Number(session?.progressPercentage || 0),
        }));
        setStats(session?.stats || {});

        setIsComparing(String(session?.status || '').trim() === 'comparing');
        setIsImporting(String(session?.status || '').trim() === 'importing');
        const hasDryRun = Boolean(session?.resultData?.dryRun);
        const pct = Number(session?.progressPercentage || 0);
        setIsDryRunning(!hasDryRun && pct >= 55 && pct < 75 && String(session?.status || '').trim() !== 'failed');

        const hasCookie = Boolean(session?.regiondoAuth?.cookieHeader);
        const usernameHint = session?.regiondoAuth?.usernameHint ? String(session.regiondoAuth.usernameHint) : '';
        setRegiondoAuthInfo({ hasCookie, usernameHint });
        if (hasCookie) {
          if (usernameHint) setRegiondoUsername(usernameHint);
          setRegiondoPassword('');
          setIsEditingRegiondoLogin(false);
        }

        if (session?.matching) {
          const serverDraft = normalizeMatchingDraftFromServer(session.matching);
          setAiSuggestions(session.matching.aiSuggestions || null);
          setMatchingDraft(prev => {
            if (matchingDirtyRef.current) return prev;
            if (hasAnyMatchingDraft(prev) && !hasAnyMatchingDraft(serverDraft)) return prev;
            return serverDraft;
          });
        }

        if (session?.resultData?.dryRun) {
          setDryRunReport(session.resultData.dryRun);
        }
        if (session?.resultData?.import) {
          setImportReport(session.resultData.import);
        }
      } finally {
        setIsLoadingSession(false);
      }
    },
    [auth?.token, sendRequest]
  );

  const compareHeader = useMemo(() => {
    const st = String(sessionMeta?.status || '').trim();
    const pct = Math.max(0, Math.min(100, Number(sessionMeta?.progressPercentage || 0)));
    if (!sessionId) return { progress: 0, title: 'Bereit' };
    if (st === 'comparing' || st === 'created') return { progress: Math.min(25, pct || 2), title: 'Vergleich läuft…' };
    if (st === 'failed') return { progress: 0, title: 'Fehler' };
    if (st) return { progress: Math.min(25, pct || 25), title: 'Vergleich fertig' };
    return { progress: Math.min(25, pct), title: status?.title || 'Vergleich' };
  }, [sessionId, sessionMeta?.progressPercentage, sessionMeta?.status, status?.title]);

  const headerProgress = useMemo(() => {
    if (stepKey === 'compare') return compareHeader;
    return { progress: Math.max(0, Math.min(100, Number(status?.progress || 0))), title: status?.title || '' };
  }, [compareHeader, status?.progress, status?.title, stepKey]);

  const loadSavedSessions = useCallback(async () => {
    if (!selectedBranchId) return;
    setIsLoadingSavedSessions(true);
    try {
      const r = await sendRequest({
        url: `/api/v1/regiondoVoucherMigration/sessions?branchId=${encodeURIComponent(selectedBranchId)}&limit=40`,
        method: 'GET',
        headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token },
      });
      setSavedSessions(Array.isArray(r?.sessions) ? r.sessions : []);
    } catch (e) {
      toast.error(e?.message || 'Sessions konnten nicht geladen werden.');
    } finally {
      setIsLoadingSavedSessions(false);
    }
  }, [auth?.token, selectedBranchId, sendRequest]);

  const loadMatchingContext = useCallback(
    async sid => {
      const s = String(sid || '').trim();
      if (!s) return;
      try {
        const r = await sendRequest({
          url: `/api/v1/regiondoVoucherMigration/session/${s}/matching/context`,
          method: 'GET',
          headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token },
        });
        setMatchingContext(r?.context || null);
        setResourceVoucherProducts(Array.isArray(r?.resourceVoucherProducts) ? r.resourceVoucherProducts : []);
        setAiSuggestions(r?.context?.matching?.aiSuggestions || null);
      } catch (e) {
        toast.error(e?.message || 'Matching context konnte nicht geladen werden.');
      }
    },
    [auth?.token, sendRequest]
  );

  useEffect(() => {
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    loadMatchingContext(sid);
  }, [sessionId, loadMatchingContext]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    if (stepKey !== 'match') return;
    loadSavedSessions();
  }, [loadSavedSessions, stepKey]);

  useEffect(() => {
    if (!hasRight) return;
    if (!sessionId) return;
    joinSessionRoom(sessionId);
  }, [hasRight, joinSessionRoom, sessionId]);

  useEffect(() => {
    writeLS('regiondoVoucherMigration.branchId', selectedBranchId);
  }, [selectedBranchId, writeLS]);

  useEffect(() => {
    if (!hasRight) return;
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    if (!isConnected) return;
    emit('join_migration', { sessionId: sid });
  }, [hasRight, sessionId, isConnected, emit]);

  useEffect(() => {
    if (!hasRight) return;
    if (!selectedBranchId) return;
    const f = async () => {
      try {
        const r = await sendRequest({
          url: `/api/v1/regiondoVoucherMigration/sessions?branchId=${encodeURIComponent(
            selectedBranchId
          )}&limit=1&sort=editedAt`,
          method: 'GET',
          headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token },
        });
        const latest = Array.isArray(r?.sessions) ? r.sessions[0] : null;
        if (!latest?.sessionId) return;
        const latestId = String(latest.sessionId);
        setSessionId(prev => {
          const cur = String(prev || '').trim();
          if (cur === latestId) return prev;
          return latestId;
        });
        loadSession(latestId);
      } catch (_) {}
    };
    f();
  }, [hasRight, selectedBranchId, sessionId, loadSession, sendRequest, auth?.token]);

  useEffect(() => {
    if (!hasRight) return;

    const handler = payload => {
      const sid = payload?.sessionId ? String(payload.sessionId) : null;
      if (sid && sessionId && sid !== String(sessionId)) return;
      if (payload?.status) setStatus(s => ({ ...s, ...payload.status }));
      if (payload?.progressInfo) setProgressInfo(payload.progressInfo);
      if (payload?.stats) setStats(payload.stats);
      if (payload?.log) pushLog(payload.log);
      if (payload?.error) setError(payload.error);
      if (payload?.dryRunReport) {
        setDryRunReport(prev => ({
          ...(prev || {}),
          ...(payload.dryRunReport || {}),
          items: payload.dryRunReport?.items || prev?.items,
        }));
      }
      if (payload?.importReport) setImportReport(payload.importReport);
      if (payload?.aiSuggest?.status && payload.aiSuggest.status !== 'running') {
        setIsAiSuggesting(false);
        if (payload.aiSuggest.status === 'done') {
          const reloadSid = sid || (sessionId ? String(sessionId) : '');
          if (reloadSid) loadMatchingContext(reloadSid);
        }
      }

      if (payload?.status) {
        if (isCompareRunningFromStatus(payload.status)) setIsComparing(true);

        if (isDryRunRunningFromStatus(payload.status)) setIsDryRunning(true);
        if (isImportRunningFromStatus(payload.status)) setIsImporting(true);

        const title = String(payload.status?.title || '').toLowerCase();
        if (title.includes('vergleich fertig')) setIsComparing(false);
        if (title.includes('fehler')) setIsComparing(false);
        if (title.includes('dry-run fertig') || title.includes('dry run fertig')) setIsDryRunning(false);
        if (title.includes('import fertig')) setIsImporting(false);
      }

      if (payload?.dryRunReport) setIsDryRunning(false);
      if (payload?.importReport) setIsImporting(false);
      if (payload?.error) {
        setIsComparing(false);
        setIsDryRunning(false);
        setIsImporting(false);
      }

      if (payload?.status) {
        const phase = String(payload.status?.phase || '').trim();
        const title = String(payload.status?.title || '').toLowerCase();
        if (phase === 'compare' && title.includes('vergleich fertig')) {
          const reloadSid = sid || (sessionId ? String(sessionId) : '');
          if (reloadSid) loadMatchingContext(reloadSid);
        }
      }
    };

    on('migrationUpdate', handler);
    return () => off('migrationUpdate', handler);
  }, [hasRight, loadMatchingContext, off, on, pushLog, sessionId]);

  const canStart = useMemo(() => {
    return Boolean(selectedBranchId && dateFrom && dateTo && regiondoUsername && regiondoPassword);
  }, [dateFrom, dateTo, regiondoPassword, regiondoUsername, selectedBranchId]);

  const canSaveRegiondoLogin = useMemo(() => {
    if (!sessionId) return false;
    if (!regiondoUsername || !regiondoPassword) return false;
    return true;
  }, [regiondoPassword, regiondoUsername, sessionId]);

  const startSession = useCallback(async () => {
    if (!canStart) return;
    setError(null);
    setDryRunReport(null);
    setImportReport(null);
    setLogs([]);
    setIsStarting(true);
    try {
      const r = await sendRequest({
        url: '/api/v1/regiondoVoucherMigration/session',
        method: 'POST',
        headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token, 'Content-Type': 'application/json' },
        body: {
          branchId: selectedBranchId,
          dateFrom,
          dateTo,
          regiondoUsername,
          regiondoPassword,
        },
      });
      if (!r?.success || !r?.sessionId) {
        throw new Error('Session konnte nicht gestartet werden.');
      }
      const sid = String(r.sessionId);
      setSessionId(sid);
      toast.success('Session gestartet');
      joinSessionRoom(sid);
      await loadSession(sid);
      await loadMatchingContext(sid);
    } catch (e) {
      if (e?.statusCode === 429 || e?.code === 'REGIONDO_TOO_MANY_REQUESTS') {
        toast.error('Zu viele Anfragen bei Regiondo. Bitte 1–2 Minuten warten und erneut versuchen.');
        setError('Zu viele Anfragen bei Regiondo. Bitte 1–2 Minuten warten und erneut versuchen.');
      } else {
        toast.error(e?.message || 'Start fehlgeschlagen.');
        setError(e?.message || 'Start fehlgeschlagen.');
      }
    } finally {
      setIsStarting(false);
    }
  }, [
    auth?.token,
    canStart,
    dateFrom,
    dateTo,
    joinSessionRoom,
    loadMatchingContext,
    loadSession,
    regiondoPassword,
    regiondoUsername,
    selectedBranchId,
    sendRequest,
  ]);

  const saveRegiondoLogin = useCallback(async () => {
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    if (!regiondoUsername || !regiondoPassword) return;
    setIsRegiondoLoggingIn(true);
    try {
      const r = await sendRequest({
        url: `/api/v1/regiondoVoucherMigration/session/${sid}/login`,
        method: 'POST',
        headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token, 'Content-Type': 'application/json' },
        body: { regiondoUsername, regiondoPassword },
      });
      const hasCookie = Boolean(r?.regiondoAuth?.hasCookie);
      const usernameHint = r?.regiondoAuth?.usernameHint ? String(r.regiondoAuth.usernameHint) : String(regiondoUsername);
      setRegiondoAuthInfo({ hasCookie, usernameHint });
      if (hasCookie) {
        setRegiondoUsername(usernameHint);
        setRegiondoPassword('');
        setIsEditingRegiondoLogin(false);
        toast.success('Regiondo Login gespeichert');
        await loadSession(sid);
      } else {
        toast.error('Regiondo Login fehlgeschlagen');
      }
    } catch (e) {
      if (e?.statusCode === 429 || e?.code === 'REGIONDO_TOO_MANY_REQUESTS') {
        toast.error('Zu viele Anfragen bei Regiondo. Bitte 1–2 Minuten warten und erneut versuchen.');
      } else {
        toast.error(e?.message || 'Regiondo Login fehlgeschlagen');
      }
    } finally {
      setIsRegiondoLoggingIn(false);
    }
  }, [auth?.token, loadSession, regiondoPassword, regiondoUsername, sendRequest, sessionId]);

  const refreshCompare = useCallback(async () => {
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    if (!selectedBranchId || !dateFrom || !dateTo) return;
    setError(null);
    setIsRefreshingCompare(true);
    setStatus(s => ({
      ...s,
      phase: 'compare',
      progress: Math.max(1, Number(s?.progress || 0)),
      title: 'Vergleich läuft…',
      details: `Daten werden neu geladen (${dateFrom} → ${dateTo}). Bitte warten…`,
    }));
    try {
      pushLog(`Vergleich neu starten (${dateFrom} → ${dateTo})…`);

      setMatchingContext(null);
      setDryRunReport(null);
      setImportReport(null);
      setAiSuggestions(null);
      lastAppliedAiSigRef.current = '';

      await sendRequest({
        url: `/api/v1/regiondoVoucherMigration/session/${sid}/compare`,
        method: 'POST',
        headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token, 'Content-Type': 'application/json' },
        body: { dateFrom, dateTo },
      });

      toast.info('Vergleich wird neu geladen…');
      await loadSession(sid);
    } catch (e) {
      toast.error(e?.message || 'Vergleich konnte nicht neu gestartet werden.');
    } finally {
      setIsRefreshingCompare(false);
    }
  }, [auth?.token, dateFrom, dateTo, loadSession, pushLog, selectedBranchId, sendRequest, sessionId]);

  const selectedBranch = useMemo(() => {
    return branches.find(b => String(b?._id) === String(selectedBranchId)) || null;
  }, [branches, selectedBranchId]);

  const isSelectedBranchCompleted = Boolean(selectedBranch?.regiondoVoucherMigration?.completedAt);

  const applySavedMatchingOnly = useCallback(async () => {
    const sourceSessionId = String(savedMatchingSessionId || '').trim();
    if (!sourceSessionId) return;
    if (!sessionId) return;
    setIsApplyingSavedMatchingOnly(true);
    try {
      const r = await sendRequest({
        url: `/api/v1/regiondoVoucherMigration/session/${sourceSessionId}/matching`,
        method: 'GET',
        headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token },
      });
      if (!r?.success) throw new Error('Matching konnte nicht geladen werden.');
      const m = r?.matching || {};
      setMatchingDraft({
        resourceVoucherProductByVoucherTypeKey: m.resourceVoucherProductByVoucherTypeKey || {},
        resourceVoucherProductByVoucherTypePriceKey: m.resourceVoucherProductByVoucherTypePriceKey || {},
        ignoredVoucherTypeKeys: m.ignoredVoucherTypeKeys || {},
      });
      matchingDirtyRef.current = false;
      toast.success('Matching übernommen');
    } catch (e) {
      toast.error(e?.message || 'Matching konnte nicht übernommen werden.');
    } finally {
      setIsApplyingSavedMatchingOnly(false);
    }
  }, [auth?.token, savedMatchingSessionId, sendRequest, sessionId]);

  const runAiSuggest = useCallback(async () => {
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    setError(null);
    setIsAiSuggesting(true);
    try {
      pushLog('AI Matching: queued…');
      await sendRequest({
        url: `/api/v1/regiondoVoucherMigration/session/${sid}/matching/suggest`,
        method: 'POST',
        headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token },
      });
      toast.info('AI Matching läuft im Hintergrund…');
    } catch (e) {
      toast.error(e?.message || 'AI Vorschläge fehlgeschlagen');
      setIsAiSuggesting(false);
    } finally {
      // keep disabled until backend signals completion via socket
    }
  }, [auth, pushLog, sendRequest, sessionId]);

  const voucherTypes = useMemo(() => {
    return Array.isArray(matchingContext?.voucherTypes) ? matchingContext.voucherTypes : [];
  }, [matchingContext?.voucherTypes]);

  const voucherTypePriceKeysByVoucherTypeKey = useMemo(() => {
    const out = new Map();
    for (const vt of voucherTypes) {
      const k = String(vt?.voucherTypeKey || '').trim();
      if (!k) continue;
      const samples = Array.isArray(vt?.price_incl_tax_samples) ? vt.price_incl_tax_samples : [];
      const distinctPrices = [...new Set(samples.map(p => Number(p)).filter(n => Number.isFinite(n) && n > 0))].sort(
        (a, b) => a - b
      );
      if (distinctPrices.length < 2) continue;
      out.set(
        k,
        distinctPrices.map(price => voucherTypePriceKey(k, price)).filter(Boolean)
      );
    }
    return out;
  }, [voucherTypes]);

  const isChecksRunning = useMemo(() => {
    const phase = String(status?.phase || '').trim();
    const pct = Number(status?.progress || 0);
    if (phase === 'checks' && pct < 75) return true;
    if (isDryRunning) return true;
    if (progressInfo && String(progressInfo?.kind || '').trim() === 'dryRun') {
      const processed = Number(progressInfo?.processed);
      const total2 = Number(progressInfo?.total);
      if (Number.isFinite(processed) && Number.isFinite(total2) && total2 > 0 && processed < total2) return true;
    }
    return false;
  }, [isDryRunning, progressInfo, status?.phase, status?.progress]);

  useEffect(() => {
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    if (!hasRight) return;

    const resume = () => {
      const now = Date.now();
      if (now - lastResumeAutoStepAtRef.current < 1500) return;
      lastResumeAutoStepAtRef.current = now;
      userStepOverrideRef.current = false;
      loadSession(sid);
    };

    const onFocus = () => resume();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resume();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [hasRight, loadSession, sessionId]);

  const checksProgress = useMemo(() => {
    if (progressInfo && String(progressInfo?.kind || '').trim() === 'dryRun') {
      const processed = Number(progressInfo?.processed);
      const total2 = Number(progressInfo?.total);
      if (Number.isFinite(processed) && Number.isFinite(total2) && total2 > 0) {
        return Math.max(0, Math.min(100, Math.round((processed / total2) * 100)));
      }
    }
    if (isChecksRunning) return 0;
    return Math.max(0, Math.min(100, Number(status?.progress || 0)));
  }, [isChecksRunning, progressInfo, status?.progress]);

  const checksDetails = useMemo(() => {
    const detailsFromStatus = String(status?.details || '').trim();
    const detailsFromProgressInfo =
      progressInfo && String(progressInfo?.kind || '').trim() === 'dryRun'
        ? (() => {
            const processed = Number(progressInfo?.processed);
            const total2 = Number(progressInfo?.total);
            if (!Number.isFinite(processed) || !Number.isFinite(total2) || total2 <= 0) return '';
            return `${processed}/${total2} geprüft`;
          })()
        : '';
    return detailsFromProgressInfo || detailsFromStatus;
  }, [progressInfo, status?.details]);

  const dryRunItems = useMemo(() => {
    return Array.isArray(dryRunReport?.items) ? dryRunReport.items : [];
  }, [dryRunReport?.items]);

  const dryRunGroups = useMemo(() => {
    const groups = {
      willImport: [],
      missingTypeMapping: [],
      alreadyImported: [],
      used: [],
      ignoredType: [],
      scrapeErrors: [],
      other: [],
      all: [],
    };

    for (const it of dryRunItems) {
      groups.all.push(it);
      if (it?.error) {
        groups.scrapeErrors.push(it);
        continue;
      }
      const skipped = String(it?.skipped || '').trim();
      if (skipped === 'missingTypeMapping') {
        groups.missingTypeMapping.push(it);
        continue;
      }
      if (skipped === 'alreadyImported') {
        groups.alreadyImported.push(it);
        continue;
      }
      if (skipped === 'ignoredType') {
        groups.ignoredType.push(it);
        continue;
      }
      if (it?.isUsed === true) {
        groups.used.push(it);
        continue;
      }
      if (it?.isUsed === false || it?.parsed) {
        groups.willImport.push(it);
        continue;
      }
      groups.other.push(it);
    }

    const counts = Object.fromEntries(Object.entries(groups).map(([k, arr]) => [k, arr.length]));
    return { groups, counts };
  }, [dryRunItems]);

  const openDryRunDetails = useCallback(
    (tab = 'summary', ticketCode = '') => {
      setDryRunDetailsTab(String(tab || 'summary'));
      setDryRunSearch('');
      setDryRunSelectedTicketCode(ticketCode ? String(ticketCode) : '');
      setDryRunDetailsOpen(true);
    },
    []
  );

  const selectedDryRunItem = useMemo(() => {
    const key = String(dryRunSelectedTicketCode || '').trim().toUpperCase();
    if (!key) return null;
    return dryRunItems.find(it => String(it?.ticketCode || '').trim().toUpperCase() === key) || null;
  }, [dryRunItems, dryRunSelectedTicketCode]);

  const activeDryRunList = useMemo(() => {
    const tab = String(dryRunDetailsTab || '').trim();
    const raw =
      tab === 'summary'
        ? []
        : dryRunGroups?.groups?.[tab]
          ? dryRunGroups.groups[tab]
          : tab === 'all'
            ? dryRunGroups?.groups?.all || []
            : dryRunGroups?.groups?.all || [];

    const q = String(dryRunSearch || '').trim().toLowerCase();
    if (!q) return raw;
    return raw.filter(it => {
      const tc = String(it?.ticketCode || '').toLowerCase();
      const vk = String(it?.voucherTypeKey || '').toLowerCase();
      return tc.includes(q) || vk.includes(q);
    });
  }, [dryRunDetailsTab, dryRunGroups, dryRunSearch]);

  useEffect(() => {
    if (!dryRunDetailsOpen) return;
    if (dryRunDetailsTab === 'summary') return;
    const cur = String(dryRunSelectedTicketCode || '').trim();
    if (cur) return;
    const first = activeDryRunList?.[0];
    if (first?.ticketCode) setDryRunSelectedTicketCode(String(first.ticketCode));
  }, [activeDryRunList, dryRunDetailsOpen, dryRunDetailsTab, dryRunSelectedTicketCode]);

  const persistMatchingDraft = useCallback(
    async (sid, draft, { successToastLabel } = {}) => {
      const s = String(sid || '').trim();
      if (!s) return false;
      setIsSavingMatching(true);
      try {
        await sendRequest({
          url: `/api/v1/regiondoVoucherMigration/session/${s}/matching`,
          method: 'PUT',
          headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token, 'Content-Type': 'application/json' },
          body: { matching: draft },
        });
        if (successToastLabel) toast.success(successToastLabel);
        matchingDirtyRef.current = false;
        await loadSession(s);
        await loadMatchingContext(s);
        return true;
      } catch (e) {
        toast.error(e?.message || 'Matching speichern fehlgeschlagen.');
        return false;
      } finally {
        setIsSavingMatching(false);
      }
    },
    [auth?.token, loadMatchingContext, loadSession, sendRequest]
  );

  useEffect(() => {
    if (!aiSuggestions) return;
    let sig = '';
    try {
      sig = JSON.stringify(aiSuggestions);
    } catch (_) {
      sig = String(Date.now());
    }
    if (sig && lastAppliedAiSigRef.current === sig) return;
    lastAppliedAiSigRef.current = sig;

    const { nextDraft, didChange } = mergeAiSuggestionsIntoDraft(
      matchingDraft,
      aiSuggestions,
      voucherTypePriceKeysByVoucherTypeKey
    );
    if (!didChange) return;

    matchingDirtyRef.current = true;
    setMatchingDraft(nextDraft);

    if (lastAutoSavedAiSigRef.current === sig) return;
    lastAutoSavedAiSigRef.current = sig;
    if (!sessionId) return;
    persistMatchingDraft(sessionId, nextDraft, { successToastLabel: 'AI Matching gespeichert' });
  }, [aiSuggestions, matchingDraft, persistMatchingDraft, sessionId, voucherTypePriceKeysByVoucherTypeKey]);

  const goPrev = useCallback(() => {
    userStepOverrideRef.current = true;
    setActiveStep(s => Math.max(0, s - 1));
  }, []);

  const goNext = useCallback(() => {
    userStepOverrideRef.current = true;
    setActiveStep(s => Math.min(steps.length - 1, s + 1));
  }, []);

  const canGoNext = useMemo(() => {
    if (activeStep === 0) return Boolean(sessionId);
    if (activeStep === 1) return Boolean(matchingContext?.matching?.lockedAt);
    if (activeStep === 2) return Boolean(dryRunReport);
    return false;
  }, [activeStep, dryRunReport, matchingContext?.matching?.lockedAt, sessionId]);

  const saveMatching = useCallback(async () => {
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    await persistMatchingDraft(sid, matchingDraft, { successToastLabel: 'Matching gespeichert' });
  }, [matchingDraft, persistMatchingDraft, sessionId]);

  const startDryRun = useCallback(async () => {
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    setIsDryRunning(true);
    try {
      await sendRequest({
        url: `/api/v1/regiondoVoucherMigration/session/${sid}/dryrun`,
        method: 'POST',
        headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token },
      });
      toast.info('Dry-Run gestartet');
    } catch (e) {
      toast.error(e?.message || 'Dry-Run konnte nicht gestartet werden.');
      setIsDryRunning(false);
    } finally {
      // keep running state until socket update signals completion
    }
  }, [auth?.token, sendRequest, sessionId]);

  const startImport = useCallback(async () => {
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    setIsImporting(true);
    try {
      await sendRequest({
        url: `/api/v1/regiondoVoucherMigration/session/${sid}/import`,
        method: 'POST',
        headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token },
      });
      toast.info('Import gestartet');
    } catch (e) {
      toast.error(e?.message || 'Import konnte nicht gestartet werden.');
      setIsImporting(false);
    } finally {
      // keep running state until socket update signals completion
    }
  }, [auth?.token, sendRequest, sessionId]);

  const saveValidatedUsedTicketCodes = useCallback(
    async codesLowercase => {
      const sid = String(sessionId || '').trim();
      if (!sid) return;
      const codes = Array.isArray(codesLowercase) ? codesLowercase : [];
      setIsSavingValidatedUsed(true);
      try {
        const r = await sendRequest({
          url: `/api/v1/regiondoVoucherMigration/session/${sid}/validated-used`,
          method: 'POST',
          headers: auth?.getAuthHeaders?.() || { Authorization: 'Bearer ' + auth?.token, 'Content-Type': 'application/json' },
          body: { ticketCodes: codes, sourceLabel: 'regiondo-validated-xls' },
        });
        if (!r?.success) throw new Error('Speichern fehlgeschlagen.');
        const count = Number(r?.validatedUsed?.count ?? codes.length);
        setValidatedUsedInfo({
          count: Number.isFinite(count) ? count : codes.length,
          updatedAt: r?.validatedUsed?.updatedAt || null,
          updatedBy: r?.validatedUsed?.updatedBy || null,
          sourceLabel: r?.validatedUsed?.sourceLabel ? String(r.validatedUsed.sourceLabel) : 'regiondo-validated-xls',
        });
        toast.success(`XLS gespeichert (${Number.isFinite(count) ? count : codes.length} Codes)`);
        await loadSession(sid);
        return true;
      } catch (e) {
        toast.error(e?.message || 'XLS konnte nicht gespeichert werden.');
        return false;
      } finally {
        setIsSavingValidatedUsed(false);
      }
    },
    [auth?.token, loadSession, sendRequest, sessionId]
  );

  const handleUploadValidatedUsedXls = useCallback(async () => {
    const file = validatedUsedFileInputRef.current?.files?.[0];
    if (!sessionId) {
      toast.error('Bitte zuerst eine Session starten.');
      return;
    }
    if (!file) {
      toast.error('Bitte eine XLS-Datei auswählen.');
      return;
    }
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheetName = Array.isArray(wb?.SheetNames) && wb.SheetNames.length ? wb.SheetNames[0] : null;
      if (!sheetName) throw new Error('XLS konnte nicht gelesen werden.');
      const sheet = wb.Sheets?.[sheetName];
      if (!sheet) throw new Error('XLS konnte nicht gelesen werden.');

      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
      const codes = extractRegiondoValidatedTicketCodesFromRows(rows);
      if (codes.length === 0) {
        toast.error('Keine Ticketcodes in der XLS gefunden.');
        return;
      }
      await saveValidatedUsedTicketCodes(codes);
    } catch (e) {
      toast.error(e?.message || 'XLS konnte nicht verarbeitet werden.');
    }
  }, [saveValidatedUsedTicketCodes, sessionId]);

  const rvpById = useMemo(() => {
    const m = new Map();
    for (const p of resourceVoucherProducts) {
      if (p && p._id) m.set(String(p._id), p);
    }
    return m;
  }, [resourceVoucherProducts]);

  if (!hasRight) {
    return (
      <Box sx={{ ...viewRootTextPrimarySx, p: 3 }}>
        <Alert severity='warning'>
          Dir fehlt das Recht{' '}
          <Box component='span' sx={{ fontWeight: 600 }}>
            internalBooking:migration:regiondo
          </Box>
          . Bitte Rolle anpassen.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant='h4' sx={{ fontWeight: 800, letterSpacing: -0.6 }}>
            Regiondo Voucher Migration
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Flextickets (Regiondo) → ResourceVoucher (intern). Importiert nur ungenutzte Voucher.
          </Typography>
        </Box>
        <Chip
          label={isConnected ? 'Live verbunden' : 'Offline'}
          color={isConnected ? 'success' : 'default'}
          variant={isConnected ? 'filled' : 'outlined'}
        />
      </Box>

      <input ref={validatedUsedFileInputRef} type='file' accept='.xls,.xlsx' style={{ display: 'none' }} />

      <Stepper
        nonLinear
        activeStep={activeStep}
        sx={{
          mb: 2,
          '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' },
          '& .MuiStepIcon-root.Mui-completed': { color: 'primary.main' },
          '& .MuiStepLabel-label.Mui-active': { color: 'primary.main', fontWeight: 700 },
          '& .MuiStepLabel-label.Mui-completed': { color: 'primary.main', fontWeight: 700 },
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: 'primary.main' },
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: 'primary.main' },
        }}
      >
        {steps.map((s, idx) => (
          <Step key={s.key} completed={idx < activeStep}>
            <StepButton
              onClick={() => {
                userStepOverrideRef.current = true;
                setActiveStep(idx);
              }}
            >
              <StepLabel>{s.title}</StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>

      {error ? (
        <Alert severity='error' sx={{ mb: 2 }}>
          {String(error)}
        </Alert>
      ) : null}

      {stepKey === 'compare' ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <StatCard
              title='Kandidaten'
              value={stats?.totalVoucherCandidates ?? 0}
              subtitle='Regiondo'
              color='primary'
              isDone={Number(stats?.totalVoucherCandidates || 0) > 0}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              title='Ungenutzt'
              value={dryRunReport ? dryRunReport?.counters?.unused ?? stats?.unusedCandidates ?? 0 : '—'}
              subtitle={dryRunReport ? 'Dry-Run' : 'Noch kein Dry-Run'}
              color='success'
              isDone={dryRunReport ? Number(dryRunReport?.counters?.unused || stats?.unusedCandidates || 0) > 0 : false}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              title='Bereits importiert'
              value={dryRunReport ? dryRunReport?.counters?.alreadyImported ?? stats?.alreadyImported ?? 0 : '—'}
              subtitle={dryRunReport ? 'Konflikte' : 'Noch kein Dry-Run'}
              color='warning'
              isDone={dryRunReport ? Number(dryRunReport?.counters?.alreadyImported || stats?.alreadyImported || 0) > 0 : false}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              title='Fortschritt'
              value={`${headerProgress?.progress ?? 0}%`}
              subtitle={headerProgress?.title || ''}
              isDone={Number(headerProgress?.progress || 0) >= 100}
            />
          </Grid>
        </Grid>
      ) : null}

      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant='h6' sx={{ fontWeight: 800 }}>
            {steps[activeStep]?.title}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {status?.details || ''}
          </Typography>
      {stepKey === 'compare' && (isRefreshingCompare || isComparing) ? (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
            </Box>
          ) : null}

          {stepKey === 'compare' ? (
            <>
            <Box sx={{ mb: 2 }}>
              <Alert severity='info'>
                <Typography variant='subtitle2' sx={{ fontWeight: 800 }}>
                  Bereits eingelöste Tickets (Regiondo „validated“ Export)
                </Typography>
                <Typography variant='body2' sx={{ mt: 0.5 }}>
                  Manche Tickets sind bereits eingelöst, werden aber im Scraping nicht als „used“ erkannt (alternativer Einlöseweg). In dem
                  Fall bitte im Regiondo Interface unter{' '}
                  <a href='https://www.regiondo.de/resellers/account/validated' target='_blank' rel='noreferrer'>
                    https://www.regiondo.de/resellers/account/validated
                  </a>{' '}
                  als XLS exportieren und hier hochladen. Ticketcodes sind in der Datei lowercase gespeichert.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                  <Button
                    variant='outlined'
                    disabled={!sessionId || isSavingValidatedUsed}
                    onClick={() => validatedUsedFileInputRef.current?.click?.()}
                  >
                    XLS auswählen
                  </Button>
                  <Button variant='contained' disabled={!sessionId || isSavingValidatedUsed} onClick={handleUploadValidatedUsedXls}>
                    {isSavingValidatedUsed ? <CircularProgress size={18} /> : 'XLS einlesen & speichern'}
                  </Button>
                  <Button
                    variant='text'
                    color='warning'
                    disabled={!sessionId || isSavingValidatedUsed || validatedUsedInfo.count === 0}
                    onClick={() => saveValidatedUsedTicketCodes([])}
                  >
                    Liste löschen
                  </Button>
                  <Chip size='small' label={`Gespeichert: ${validatedUsedInfo.count || 0}`} />
                </Box>
                {validatedUsedInfo.updatedAt ? (
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.75 }}>
                    Letztes Update: {dayjs(validatedUsedInfo.updatedAt).format('DD.MM.YYYY HH:mm')}
                  </Typography>
                ) : null}
              </Alert>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Branch</InputLabel>
                  <Select
                    value={selectedBranchId}
                    label='Branch'
                    onChange={e => setSelectedBranchId(String(e.target.value))}
                  >
                    {branches.map(b => (
                      <MenuItem key={String(b?._id)} value={String(b?._id)}>
                        {b?.longName || b?.locationName || String(b?._id).slice(-6)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={<Checkbox checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />}
                  label='Auch abgeschlossene anzeigen'
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label='Von'
                  type='date'
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label='Bis'
                  type='date'
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4} />

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label='Regiondo Username'
                  value={regiondoUsername}
                  onChange={e => setRegiondoUsername(e.target.value)}
                  disabled={!isRegiondoLoginEditable}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type='password'
                  label='Regiondo Password'
                  value={isRegiondoAlreadyAuthenticated && !isEditingRegiondoLogin ? '********' : regiondoPassword}
                  onChange={e => setRegiondoPassword(e.target.value)}
                  disabled={!isRegiondoLoginEditable}
                />
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'stretch', gap: 1 }}>
                <Button
                  fullWidth
                  variant='contained'
                  disabled={isRegiondoAlreadyAuthenticated || !canStart || isStarting}
                  onClick={startSession}
                  sx={{ height: 56, minHeight: 56 }}
                >
                  {isRegiondoAlreadyAuthenticated ? 'Bereits angemeldet' : isStarting ? <CircularProgress size={18} /> : 'Session starten'}
                </Button>
                <Button
                  fullWidth
                  variant='outlined'
                  disabled={!sessionId || isStarting || isRefreshingCompare || !dateFrom || !dateTo}
                  onClick={refreshCompare}
                  sx={{ height: 56, minHeight: 56 }}
                >
                  {isRefreshingCompare ? <CircularProgress size={18} /> : 'Daten neu holen'}
                </Button>
                <Button
                  fullWidth
                  variant={isEditingRegiondoLogin ? 'contained' : 'outlined'}
                  color={isEditingRegiondoLogin ? 'warning' : 'inherit'}
                  disabled={!sessionId || isStarting || isRefreshingCompare || isRegiondoLoggingIn}
                  onClick={() => {
                    if (!sessionId) return;
                    if (isEditingRegiondoLogin) {
                      setIsEditingRegiondoLogin(false);
                      setRegiondoPassword('');
                      return;
                    }
                    setIsEditingRegiondoLogin(true);
                    setRegiondoPassword('');
                  }}
                  sx={{ height: 56, minHeight: 56 }}
                >
                  {isEditingRegiondoLogin ? 'Abbrechen' : 'Neu anmelden'}
                </Button>
              </Grid>
              {isEditingRegiondoLogin ? (
                <Grid item xs={12} md={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: -0.5 }}>
                  <Button
                    variant='contained'
                    disabled={!canSaveRegiondoLogin || isRegiondoLoggingIn}
                    onClick={saveRegiondoLogin}
                  >
                    {isRegiondoLoggingIn ? <CircularProgress size={18} /> : 'Login speichern'}
                  </Button>
                </Grid>
              ) : null}
            </Grid>
            </>
          ) : null}

          {stepKey === 'match' ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button variant='outlined' disabled={!sessionId} onClick={() => loadMatchingContext(sessionId)}>
                    Matching Context laden
                  </Button>
                  <Button variant='outlined' onClick={runAiSuggest} disabled={!sessionId || isAiSuggesting}>
                    {isAiSuggesting ? <CircularProgress size={18} /> : 'AI Vorschläge'}
                  </Button>
                  <Button variant='contained' disabled={!sessionId || isSavingMatching} onClick={saveMatching}>
                    {isSavingMatching ? <CircularProgress size={18} /> : 'Matching speichern'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label={`${voucherTypes.length} Voucher-Typen`} />
                </Box>
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!selectedBranchId || isLoadingSavedSessions}>
                    <InputLabel>Matching aus Session</InputLabel>
                    <Select
                      value={savedMatchingSessionId}
                      label='Matching aus Session'
                      onChange={e => setSavedMatchingSessionId(String(e.target.value))}
                    >
                      {!selectedBranchId ? (
                        <MenuItem value=''>
                          <em>Bitte Filiale wählen</em>
                        </MenuItem>
                      ) : isLoadingSavedSessions ? (
                        <MenuItem value=''>
                          <em>Lade…</em>
                        </MenuItem>
                      ) : savedSessions.length === 0 ? (
                        <MenuItem value=''>
                          <em>Keine Sessions</em>
                        </MenuItem>
                      ) : (
                        savedSessions.map(s => (
                          <MenuItem key={String(s.sessionId)} value={String(s.sessionId)}>
                            {String(s.sessionId).slice(-6)} • {s.dateFrom} → {s.dateTo} • {s.matching?.lockedAt ? 'Matching ✔' : 'kein Matching'} •{' '}
                            {s.status}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title='Matching übernehmen'>
                    <span>
                      <IconButton
                        size='small'
                        onClick={applySavedMatchingOnly}
                        disabled={
                          !sessionId || !savedMatchingSessionId || isApplyingSavedMatchingOnly || isLoadingSavedSessions
                        }
                        sx={{
                          width: 42,
                          height: 40,
                          borderRadius: 1.5,
                          border: vmSurface.iconBtnOutline,
                        }}
                      >
                        {isApplyingSavedMatchingOnly ? <CircularProgress size={18} /> : <CheckIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>

              {!voucherTypes.length ? (
                <Alert severity='info'>Noch keine Voucher-Typen geladen. Starte zuerst den Vergleich oder lade den Context.</Alert>
              ) : (
                <Grid container spacing={2}>
                  {voucherTypes.map(vt => {
                    const k = String(vt.voucherTypeKey);
                    const decoded = decodeKey(k);
                    const hasPriceSamples = Array.isArray(vt?.price_incl_tax_samples) && vt.price_incl_tax_samples.length > 0;
                    const allPrices = hasPriceSamples
                      ? [...new Set(vt.price_incl_tax_samples.map(p => Number(p)).filter(n => Number.isFinite(n)))].sort(
                          (a, b) => a - b
                        )
                      : [];
                    const hasZeroPrice = allPrices.some(p => p <= 0);
                    const distinctPrices = allPrices.filter(p => p > 0);
                    const hasUnitsVariants = Array.isArray(vt?.units_samples) ? vt.units_samples.length >= 2 : false;
                    const usePriceMatching = distinctPrices.length >= 2 && !hasUnitsVariants;

                    const current = matchingDraft.resourceVoucherProductByVoucherTypeKey?.[k] || '';
                    const ignored = matchingDraft.ignoredVoucherTypeKeys?.[k] === true;
                    const currentRvp = current ? rvpById.get(String(current)) : null;
                    const title =
                      vt?.ticket_name ||
                      (Array.isArray(decoded) ? String(decoded[0] || '') : '') ||
                      '—';
                    const subtitle = [vt?.variation_name, vt?.option_name].filter(Boolean).join(' • ');

                    return (
                      <Grid key={k} item xs={12}>
                        <Card variant='outlined'>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                              <Box sx={{ flex: 1, minWidth: 260 }}>
                                <Typography variant='subtitle1' sx={{ fontWeight: 800 }}>
                                  {title}
                                </Typography>
                                <Typography variant='body2' color='text.secondary'>
                                  {subtitle || '—'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                  <Chip size='small' label={`Count: ${vt?.count || 0}`} />
                                  <Chip
                                    size='small'
                                    label={`Einheiten: ${
                                      Array.isArray(vt?.units_samples) && vt.units_samples.length > 0
                                        ? [...new Set(vt.units_samples.map(n => Number(n)).filter(n => Number.isFinite(n)))]
                                            .sort((a, b) => a - b)
                                            .join(', ')
                                        : vt?.units_min != null && vt?.units_max != null && Number(vt.units_min) !== Number(vt.units_max)
                                          ? `${vt.units_min}–${vt.units_max}`
                                          : vt?.units_min || vt?.units_max || 1
                                    }`}
                                  />
                                  {vt?.price_incl_tax_min != null || vt?.price_incl_tax_max != null ? (
                                    <Chip
                                      size='small'
                                      label={
                                        vt?.price_incl_tax_min != null &&
                                        vt?.price_incl_tax_max != null &&
                                        Number(vt.price_incl_tax_min) !== Number(vt.price_incl_tax_max)
                                          ? `Preis: ${formatEUR(vt.price_incl_tax_min)} – ${formatEUR(vt.price_incl_tax_max)}`
                                          : `Preis: ${formatEUR(vt.price_incl_tax_min ?? vt.price_incl_tax_max)}`
                                      }
                                    />
                                  ) : null}
                                  {hasZeroPrice ? <Chip size='small' color='warning' label='0€-Preis erkannt (ignoriert)' /> : null}
                                  {vt?.sku ? <Chip size='small' label={`SKU: ${vt.sku}`} /> : null}
                                  {vt?.sampleTicketCodes?.length ? (
                                    <Chip size='small' label={`Samples: ${vt.sampleTicketCodes.slice(0, 3).join(', ')}`} />
                                  ) : null}
                                </Box>
                              </Box>

                              <Box sx={{ minWidth: 320 }}>
                                {!usePriceMatching ? (
                                  <FormControl fullWidth disabled={ignored}>
                                    <InputLabel>ResourceVoucherProduct</InputLabel>
                                    <Select
                                      value={current}
                                      label='ResourceVoucherProduct'
                                      onChange={e => {
                                        const val = String(e.target.value);
                                        markMatchingDirty();
                                        setMatchingDraft(prev => ({
                                          ...prev,
                                          resourceVoucherProductByVoucherTypeKey: {
                                            ...(prev.resourceVoucherProductByVoucherTypeKey || {}),
                                            [k]: val,
                                          },
                                        }));
                                      }}
                                    >
                                      <MenuItem value=''>
                                        <em>— nicht zugeordnet —</em>
                                      </MenuItem>
                                      {resourceVoucherProducts.map(p => (
                                        <MenuItem key={String(p._id)} value={String(p._id)}>
                                          {p.name} • {Math.round(Number(p.durationMS || 0) / 60000)}m • {p.personCount}p
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {distinctPrices.map(price => {
                                      const pk = voucherTypePriceKey(k, price);
                                      const cur = matchingDraft.resourceVoucherProductByVoucherTypePriceKey?.[pk] || '';
                                      const curRvp = cur ? rvpById.get(String(cur)) : null;
                                      return (
                                        <Box key={pk}>
                                          <Typography variant='caption' color='text.secondary'>
                                            Preis {formatEUR(price)}
                                          </Typography>
                                          <FormControl fullWidth disabled={ignored} size='small' sx={{ mt: 0.5 }}>
                                            <InputLabel>ResourceVoucherProduct</InputLabel>
                                            <Select
                                              value={cur}
                                              label='ResourceVoucherProduct'
                                              onChange={e => {
                                                const val = String(e.target.value);
                                                markMatchingDirty();
                                                setMatchingDraft(prev => ({
                                                  ...prev,
                                                  resourceVoucherProductByVoucherTypePriceKey: {
                                                    ...(prev.resourceVoucherProductByVoucherTypePriceKey || {}),
                                                    [pk]: val,
                                                  },
                                                }));
                                              }}
                                            >
                                              <MenuItem value=''>
                                                <em>— nicht zugeordnet —</em>
                                              </MenuItem>
                                              {resourceVoucherProducts.map(p => (
                                                <MenuItem key={String(p._id)} value={String(p._id)}>
                                                  {p.name} • {Math.round(Number(p.durationMS || 0) / 60000)}m • {p.personCount}p
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                          {curRvp ? (
                                            <Typography variant='caption' color='text.secondary'>
                                              Aktuell: {curRvp.name}
                                            </Typography>
                                          ) : null}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                )}
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={ignored}
                                      onChange={e => {
                                        const checked = e.target.checked;
                                        markMatchingDirty();
                                        setMatchingDraft(prev => ({
                                          ...prev,
                                          ignoredVoucherTypeKeys: {
                                            ...(prev.ignoredVoucherTypeKeys || {}),
                                            [k]: checked,
                                          },
                                        }));
                                      }}
                                    />
                                  }
                                  label='Typ ignorieren'
                                />
                                {currentRvp && !usePriceMatching ? (
                                  <Typography variant='caption' color='text.secondary'>
                                    Aktuell: {currentRvp.name}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </>
          ) : null}

          {stepKey === 'checks' ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Alert severity='info'>
                  <Typography variant='subtitle2' sx={{ fontWeight: 800 }}>
                    XLS „validated“ (Weg B)
                  </Typography>
                  <Typography variant='body2' sx={{ mt: 0.5 }}>
                    Upload der Regiondo XLS aus{' '}
                    <a href='https://www.regiondo.de/resellers/account/validated' target='_blank' rel='noreferrer'>
                      https://www.regiondo.de/resellers/account/validated
                    </a>{' '}
                    (Ticketcodes sind lowercase). Gespeichert:{' '}
                    <Box component='span' sx={{ fontWeight: 600 }}>
                      {validatedUsedInfo.count || 0}
                    </Box>{' '}
                    Codes.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                    <Button
                      size='small'
                      variant='outlined'
                      disabled={!sessionId || isSavingValidatedUsed}
                      onClick={() => validatedUsedFileInputRef.current?.click?.()}
                    >
                      XLS auswählen
                    </Button>
                    <Button
                      size='small'
                      variant='contained'
                      disabled={!sessionId || isSavingValidatedUsed}
                      onClick={handleUploadValidatedUsedXls}
                    >
                      {isSavingValidatedUsed ? <CircularProgress size={18} /> : 'XLS speichern'}
                    </Button>
                    <Button
                      size='small'
                      variant='text'
                      color='warning'
                      disabled={!sessionId || isSavingValidatedUsed || validatedUsedInfo.count === 0}
                      onClick={() => saveValidatedUsedTicketCodes([])}
                    >
                      Liste löschen
                    </Button>
                  </Box>
                </Alert>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Button variant='contained' disabled={!sessionId || isDryRunning || isChecksRunning} onClick={startDryRun}>
                  {isDryRunning || isChecksRunning ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} />
                      <span>Dry-Run…</span>
                    </Box>
                  ) : (
                    'Dry-Run starten'
                  )}
                </Button>
                <Button variant='outlined' disabled={!dryRunReport} onClick={() => openDryRunDetails('summary')}>
                  Details
                </Button>
                {dryRunReport ? (
                  <>
                    <Button
                      size='small'
                      variant='outlined'
                      disabled={dryRunGroups?.counts?.willImport === 0}
                      onClick={() => openDryRunDetails('willImport')}
                    >
                      Importierbar ({dryRunGroups?.counts?.willImport ?? 0})
                    </Button>
                    <Button
                      size='small'
                      variant='outlined'
                      disabled={dryRunGroups?.counts?.missingTypeMapping === 0}
                      onClick={() => openDryRunDetails('missingTypeMapping')}
                    >
                      Fehlendes Mapping ({dryRunGroups?.counts?.missingTypeMapping ?? 0})
                    </Button>
                    <Button
                      size='small'
                      variant='outlined'
                      disabled={dryRunGroups?.counts?.scrapeErrors === 0}
                      onClick={() => openDryRunDetails('scrapeErrors')}
                    >
                      Fehler ({dryRunGroups?.counts?.scrapeErrors ?? 0})
                    </Button>
                  </>
                ) : null}
              </Box>
              {isChecksRunning && (
                <Box sx={{ mt: 1.5, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant='body2' sx={{ fontWeight: 700 }}>
                      Dry-Run Fortschritt
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {checksDetails ? `${checksDetails} • ` : ''}
                      {Math.round(checksProgress)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant='determinate'
                    value={checksProgress}
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      backgroundColor: vmSurface.linearBg,
                      '& .MuiLinearProgress-bar': { borderRadius: 999 },
                    }}
                  />
                </Box>
              )}
              {!dryRunReport ? (
                <Alert severity='info'>Noch kein Dry-Run Report vorhanden.</Alert>
              ) : (
                <>
                  {Number(dryRunReport?.counters?.willImport || 0) === 0 ? (
                    <Alert severity='warning' sx={{ mb: 2 }}>
                      Dry-Run ist fertig, aber es sind{' '}
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        0
                      </Box>{' '}
                      Voucher importierbar.
                      <Box sx={{ mt: 1 }}>
                        Gründe sind meist:
                        <ul style={{ margin: '6px 0 0 16px' }}>
                          <li>alles bereits eingelöst (used)</li>
                          <li>bereits importiert</li>
                          <li>fehlendes Matching / ignorierte Typen</li>
                          <li>Scraping-Fehler (z.B. Regiondo Session abgelaufen)</li>
                        </ul>
                      </Box>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button size='small' variant='outlined' onClick={() => openDryRunDetails('scrapeErrors')}>
                          Fehler ({dryRunGroups?.counts?.scrapeErrors ?? 0})
                        </Button>
                        <Button size='small' variant='outlined' onClick={() => openDryRunDetails('missingTypeMapping')}>
                          Fehlendes Mapping ({dryRunGroups?.counts?.missingTypeMapping ?? 0})
                        </Button>
                        <Button size='small' variant='outlined' onClick={() => openDryRunDetails('used')}>
                          Used ({dryRunGroups?.counts?.used ?? 0})
                        </Button>
                        <Button size='small' variant='outlined' onClick={() => openDryRunDetails('alreadyImported')}>
                          Bereits importiert ({dryRunGroups?.counts?.alreadyImported ?? 0})
                        </Button>
                      </Box>
                    </Alert>
                  ) : (
                    <Alert severity='success' sx={{ mb: 2 }}>
                      Dry-Run fertig. Importierbar:{' '}
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        {dryRunReport?.counters?.willImport ?? 0}
                      </Box>{' '}
                      von{' '}
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        {dryRunReport?.counters?.totalCandidates ?? 0}
                      </Box>
                      .
                    </Alert>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <StatCard
                        title='Importierbar'
                        value={dryRunReport?.counters?.willImport ?? 0}
                        subtitle='Dry-Run'
                        color='success'
                        isDone={Number(dryRunReport?.counters?.willImport || 0) > 0}
                        action={
                          <IconButton size='small' onClick={() => openDryRunDetails('willImport')}>
                            <CheckIcon fontSize='small' />
                          </IconButton>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <StatCard
                        title='Fehlendes Mapping'
                        value={dryRunReport?.counters?.missingTypeMapping ?? 0}
                        subtitle='Skipped'
                        color='warning'
                        isDone={Number(dryRunReport?.counters?.missingTypeMapping || 0) === 0}
                        action={
                          <IconButton size='small' onClick={() => openDryRunDetails('missingTypeMapping')}>
                            <CheckIcon fontSize='small' />
                          </IconButton>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <StatCard
                        title='Bereits importiert'
                        value={dryRunReport?.counters?.alreadyImported ?? 0}
                        subtitle='Skipped'
                        color='warning'
                        isDone={Number(dryRunReport?.counters?.alreadyImported || 0) === 0}
                        action={
                          <IconButton size='small' onClick={() => openDryRunDetails('alreadyImported')}>
                            <CheckIcon fontSize='small' />
                          </IconButton>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <StatCard
                        title='Scrape-Fehler'
                        value={dryRunReport?.counters?.scrapeErrors ?? 0}
                        subtitle='Failed'
                        color={Number(dryRunReport?.counters?.scrapeErrors || 0) > 0 ? 'warning' : 'success'}
                        isDone={Number(dryRunReport?.counters?.scrapeErrors || 0) === 0}
                        action={
                          <IconButton size='small' onClick={() => openDryRunDetails('scrapeErrors')}>
                            <CheckIcon fontSize='small' />
                          </IconButton>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <StatCard
                        title='Eingelöst'
                        value={dryRunReport?.counters?.usedScrape ?? dryRunReport?.counters?.used ?? 0}
                        subtitle='Weg A (Scrape)'
                        color='warning'
                        isDone={Number(dryRunReport?.counters?.usedScrape ?? dryRunReport?.counters?.used ?? 0) === 0}
                        action={
                          <IconButton size='small' onClick={() => openDryRunDetails('used')}>
                            <CheckIcon fontSize='small' />
                          </IconButton>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <StatCard
                        title='Eingelöst'
                        value={dryRunReport?.counters?.usedValidated ?? 0}
                        subtitle='Weg B (XLS)'
                        color='warning'
                        isDone={Number(dryRunReport?.counters?.usedValidated || 0) === 0}
                        action={
                          <IconButton size='small' onClick={() => openDryRunDetails('used')}>
                            <CheckIcon fontSize='small' />
                          </IconButton>
                        }
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </>
          ) : null}

          {stepKey === 'import' ? (
            <>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Button variant='contained' disabled={!sessionId || isImporting} onClick={startImport}>
                  {isImporting ? <CircularProgress size={18} /> : 'Import starten'}
                </Button>
                <Button variant='outlined' disabled={!importReport} onClick={() => setImportDetailsOpen(true)}>
                  Details
                </Button>
                <Button
                  variant='outlined'
                  color={isSelectedBranchCompleted ? 'warning' : 'success'}
                  disabled={!selectedBranchId || !importReport}
                  onClick={() => {
                    setFinalizeDialogMode(isSelectedBranchCompleted ? 'undo' : 'do');
                    setFinalizeDialogOpen(true);
                  }}
                >
                  {isSelectedBranchCompleted ? 'Final zurücksetzen' : 'Als migriert markieren'}
                </Button>
              </Box>
              {!importReport ? (
                <Alert severity='info'>Noch kein Import-Report vorhanden.</Alert>
              ) : (
                <Alert severity='success'>
                  Import fertig. Erstellt:{' '}
                  <Box component='span' sx={{ fontWeight: 600 }}>
                    {importReport?.createdCount ?? 0}
                  </Box>
                  .
                </Alert>
              )}
            </>
          ) : null}

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Button variant='outlined' onClick={goPrev} disabled={activeStep === 0}>
              Zurück
            </Button>
            <Button variant='contained' onClick={goNext} disabled={activeStep >= steps.length - 1 || !canGoNext}>
              Weiter
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card variant='outlined'>
        <CardContent>
          <Typography variant='overline' color='text.secondary'>
            Live Log
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ maxHeight: 260, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
            {logs.length ? (
              logs.slice(-250).map((l, idx) => (
                <Box key={`${l.ts}-${idx}`} sx={{ whiteSpace: 'pre-wrap' }}>
                  {dayjs(l.ts).format('HH:mm:ss')}  {l.line}
                </Box>
              ))
            ) : (
              <Typography variant='body2' color='text.secondary'>
                (noch keine Logs)
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Dialog open={dryRunDetailsOpen} onClose={() => setDryRunDetailsOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>Dry-Run Details</DialogTitle>
        <DialogContent dividers>
          {!dryRunReport ? (
            <Typography variant='body2'>Kein Report.</Typography>
          ) : (
            <>
              <Tabs
                value={dryRunDetailsTab}
                onChange={(_, v) => setDryRunDetailsTab(String(v))}
                variant='scrollable'
                scrollButtons='auto'
                sx={{ mb: 2 }}
              >
                <Tab value='summary' label='Übersicht' />
                <Tab value='willImport' label={`Importierbar (${dryRunGroups?.counts?.willImport ?? 0})`} />
                <Tab value='missingTypeMapping' label={`Fehlendes Mapping (${dryRunGroups?.counts?.missingTypeMapping ?? 0})`} />
                <Tab value='alreadyImported' label={`Bereits importiert (${dryRunGroups?.counts?.alreadyImported ?? 0})`} />
                <Tab value='used' label={`Eingelöst (${dryRunGroups?.counts?.used ?? 0})`} />
                <Tab value='scrapeErrors' label={`Fehler (${dryRunGroups?.counts?.scrapeErrors ?? 0})`} />
                <Tab value='ignoredType' label={`Ignoriert (${dryRunGroups?.counts?.ignoredType ?? 0})`} />
                <Tab value='all' label={`Alle (${dryRunGroups?.counts?.all ?? 0})`} />
              </Tabs>

              {dryRunDetailsTab === 'summary' ? (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant='subtitle2' sx={{ fontWeight: 800, mb: 1 }}>
                        Counters
                      </Typography>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(dryRunReport?.counters || {}, null, 2)}
                      </pre>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant='subtitle2' sx={{ fontWeight: 800, mb: 1 }}>
                        Quick Links
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button size='small' variant='outlined' onClick={() => setDryRunDetailsTab('willImport')}>
                          Importierbar
                        </Button>
                        <Button size='small' variant='outlined' onClick={() => setDryRunDetailsTab('missingTypeMapping')}>
                          Fehlendes Mapping
                        </Button>
                        <Button size='small' variant='outlined' onClick={() => setDryRunDetailsTab('alreadyImported')}>
                          Bereits importiert
                        </Button>
                        <Button size='small' variant='outlined' onClick={() => setDryRunDetailsTab('used')}>
                          Used
                        </Button>
                        <Button size='small' variant='outlined' onClick={() => setDryRunDetailsTab('scrapeErrors')}>
                          Fehler
                        </Button>
                        <Button size='small' variant='outlined' onClick={() => setDryRunDetailsTab('ignoredType')}>
                          Ignoriert
                        </Button>
                        <Button size='small' variant='outlined' onClick={() => setDryRunDetailsTab('all')}>
                          Alle
                        </Button>
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant='subtitle2' sx={{ fontWeight: 800, mb: 1 }}>
                        Samples
                      </Typography>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(dryRunReport?.samples || {}, null, 2)}
                      </pre>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                      size='small'
                      label='Suche (ticketCode / voucherTypeKey)'
                      value={dryRunSearch}
                      onChange={e => setDryRunSearch(e.target.value)}
                      sx={{ minWidth: 320 }}
                    />
                    <Chip
                      size='small'
                      label={`${Math.min(activeDryRunList.length, 500)}/${activeDryRunList.length} angezeigt`}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <Box
                        sx={{
                          borderRadius: 2,
                          border: vmSurface.panelBorder,
                          overflow: 'auto',
                          maxHeight: 460,
                          backgroundColor: vmSurface.panelBg,
                          p: 1,
                        }}
                      >
                        {activeDryRunList.length === 0 ? (
                          <Typography variant='body2' color='text.secondary'>
                            Keine Einträge.
                          </Typography>
                        ) : (
                          activeDryRunList.slice(0, 500).map((it, idx) => {
                            const tc = String(it?.ticketCode || '').trim();
                            const selected = tc && String(dryRunSelectedTicketCode || '').trim().toUpperCase() === tc.toUpperCase();
                            const reason = renderDryRunItemReason(it);
                            const title = tc || `#${idx + 1}`;
                            const subtitle = [it?.ticket_name, it?.option_name].filter(Boolean).join(' • ');
                            const product = it?.parsed?.productName ? String(it.parsed.productName) : '';
                            return (
                              <Box
                                key={`${tc || idx}-${idx}`}
                                onClick={() => setDryRunSelectedTicketCode(tc)}
                                sx={{
                                  cursor: 'pointer',
                                  borderRadius: 2,
                                  px: 1,
                                  py: 0.9,
                                  mb: 0.75,
                                  border: selected ? vmSurface.rowBorderSel : vmSurface.rowBorder,
                                  backgroundColor: selected ? vmSurface.rowBgSel : vmSurface.rowBg,
                                }}
                              >
                                <Typography sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                  {title}  •  {reason}
                                </Typography>
                                {subtitle ? (
                                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                                    {subtitle}
                                  </Typography>
                                ) : null}
                                {product ? (
                                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                                    {product}
                                  </Typography>
                                ) : null}
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={7}>
                      {!selectedDryRunItem ? (
                        <Alert severity='info'>Wähle links einen Eintrag aus, um Details zu sehen.</Alert>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <Box>
                              <Typography variant='subtitle2' sx={{ fontWeight: 800 }}>
                                {String(selectedDryRunItem?.ticketCode || '—')}
                              </Typography>
                              <Typography variant='caption' color='text.secondary'>
                                {renderDryRunItemReason(selectedDryRunItem)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                                flexWrap: 'nowrap',
                                justifyContent: 'flex-end',
                                minWidth: 0,
                              }}
                            >
                              <Button
                                size='small'
                                variant='outlined'
                                disabled={!selectedDryRunItem?.ticketCode}
                                onClick={() => {
                                  const url = buildRegiondoExternalVoucherUrl(selectedDryRunItem?.ticketCode);
                                  if (url) window.open(url, '_blank');
                                }}
                              >
                                Regiondo öffnen
                              </Button>
                              <Tooltip title={String(selectedDryRunItem?.voucherTypeKey || '')} placement='top' arrow>
                                <Chip
                                  size='small'
                                  label={shortenMiddle(selectedDryRunItem?.voucherTypeKey || '—', 14, 10) || '—'}
                                  sx={{
                                    maxWidth: 260,
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    },
                                  }}
                                />
                              </Tooltip>
                            </Box>
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant='subtitle2' sx={{ fontWeight: 800, mb: 0.75 }}>
                                Dry-Run Item
                              </Typography>
                              <Box
                                sx={{
                                  borderRadius: 2,
                                  border: vmSurface.panelBorder,
                                  overflow: 'auto',
                                  maxHeight: 360,
                                  backgroundColor: vmSurface.panelBg,
                                  p: 1.25,
                                  fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                  fontSize: 12,
                                  lineHeight: 1.6,
                                }}
                              >
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                  {JSON.stringify(selectedDryRunItem || {}, null, 2)}
                                </pre>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant='subtitle2' sx={{ fontWeight: 800, mb: 0.75 }}>
                                Scrape/Parsed
                              </Typography>
                              <Box
                                sx={{
                                  borderRadius: 2,
                                  border: vmSurface.panelBorder,
                                  overflow: 'auto',
                                  maxHeight: 360,
                                  backgroundColor: vmSurface.panelBg,
                                  p: 1.25,
                                  fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                  fontSize: 12,
                                  lineHeight: 1.6,
                                }}
                              >
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                  {JSON.stringify(
                                    selectedDryRunItem?.parsed
                                      ? selectedDryRunItem.parsed
                                      : selectedDryRunItem?.error
                                        ? { error: selectedDryRunItem.error, needsAdditionalAuth: Boolean(selectedDryRunItem?.needsAdditionalAuth) }
                                        : {},
                                    null,
                                    2
                                  )}
                                </pre>
                              </Box>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDryRunDetailsOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDetailsOpen} onClose={() => setImportDetailsOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>Import Details</DialogTitle>
        <DialogContent dividers>
          {!importReport ? (
            <Typography variant='body2'>Kein Report.</Typography>
          ) : (
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(importReport || {}, null, 2)}</pre>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDetailsOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={finalizeDialogOpen} onClose={() => setFinalizeDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {finalizeDialogMode === 'undo' ? 'Final-Migration zurücksetzen?' : 'Branch final migriert markieren?'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2' sx={{ mb: 1 }}>
            {finalizeDialogMode === 'undo'
              ? 'Damit taucht die Filiale wieder in der Liste auf.'
              : 'Danach taucht die Filiale nicht mehr in der Liste auf (außer „Auch abgeschlossene anzeigen“ ist aktiv).'}
          </Typography>
          <Box sx={{ p: 1.25, borderRadius: 2, backgroundColor: vmSurface.finalizeBg }}>
            <Typography variant='caption' color='text.secondary'>
              Filiale
            </Typography>
            <Typography variant='body2' sx={{ fontWeight: 700 }}>
              {selectedBranch?.longName || selectedBranch?.locationName || selectedBranchId || '—'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setFinalizeDialogOpen(false)} disabled={isFinalizingBranch}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color={finalizeDialogMode === 'undo' ? 'warning' : 'success'}
            disabled={isFinalizingBranch || !selectedBranchId}
            onClick={async () => {
              if (!selectedBranchId) return;
              const completed = finalizeDialogMode !== 'undo';
              setIsFinalizingBranch(true);
              try {
                await sendRequest({
                  url: `/api/v1/regiondoVoucherMigration/branch/${selectedBranchId}/complete`,
                  method: 'POST',
                  headers: auth.getAuthHeaders(),
                  body: { completed },
                });
                toast.success(completed ? 'Als final migriert markiert' : 'Final-Migration zurückgesetzt');
                await loadBranches();
                if (completed) {
                  resetSessionUi();
                }
                setFinalizeDialogOpen(false);
              } catch (e) {
                toast.error(finalizeDialogMode === 'undo' ? 'Konnte nicht zurücksetzen' : 'Konnte nicht final markieren');
              } finally {
                setIsFinalizingBranch(false);
              }
            }}
          >
            {isFinalizingBranch ? 'Speichere…' : finalizeDialogMode === 'undo' ? 'Zurücksetzen' : 'Bestätigen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

